import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

// Must match REMEMBERED_ALIAS_KEY in Login.js, which stores the remembered
// alias in localStorage (the backend cookie is HttpOnly and unreadable by JS).
const REMEMBERED_ALIAS_KEY = 'last_workspace_alias';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [alias, setAlias] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const remembered = localStorage.getItem(REMEMBERED_ALIAS_KEY);
    if (remembered) setAlias(remembered);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!alias.trim()) {
      setError('Please enter your workspace alias.');
      return;
    }
    setError('');
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, {
        email,
        alias: alias.trim(),
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-4">
              <img
                src="/ergoniq-logo.svg"
                alt="Ergoniq Logo"
                className="h-20 w-auto"
              />
            </div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] bg-clip-text text-transparent mb-2">
              Check Your Email
            </h2>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <h3 className="font-semibold text-green-800 mb-1">Email Sent!</h3>
                <p className="text-sm text-green-700">
                  If an account exists with the email <strong>{email}</strong>, you will receive a password reset link shortly.
                </p>
                <p className="text-sm text-green-700 mt-2">
                  Please check your inbox and spam folder. The link will expire in 30 minutes.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-4 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
            >
              Back to Login
            </button>
            <button
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              className="w-full bg-white text-[#1B5C94] border-2 border-[#1B5C94] font-bold py-3 px-4 rounded-lg hover:bg-blue-50 transition-all duration-200"
            >
              Try Another Email
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p>Didn't receive an email?</p>
            <p className="mt-1">Check your spam folder or contact <a href="mailto:support@saarv.in" className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold">support@saarv.in</a></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/ergoniq-logo.svg"
              alt="Ergoniq Logo"
              className="h-20 w-auto"
            />
          </div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] bg-clip-text text-transparent mb-2">
            Forgot Password
          </h2>
          <p className="text-gray-600">Enter your workspace and email to receive a reset link</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="alias">
              Workspace alias <span className="text-red-500">*</span>
            </label>
            <input
              id="alias"
              type="text"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              placeholder="your-workspace"
              autoComplete="organization"
              autoCapitalize="none"
              spellCheck={false}
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Forgot your workspace? Contact your administrator.
            </p>
          </div>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] focus:border-transparent"
              placeholder="Enter your email"
              required
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-2">
              We'll send you a secure link to reset your password
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white font-bold py-3 px-4 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold text-sm flex items-center justify-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Login
          </Link>
        </div>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Need help? Contact <a href="mailto:support@saarv.in" className="text-[#1B5C94] hover:text-[#3AC0DB] font-semibold">support@saarv.in</a></p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
